/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { riskResultsByChat } from "../funcs/riskResultsByChat.js";
import { riskResultsList } from "../funcs/riskResultsList.js";
import { riskResultsListDismissed } from "../funcs/riskResultsListDismissed.js";
import { riskResultsListForAgent } from "../funcs/riskResultsListForAgent.js";
import { riskResultsMarkFalsePositive } from "../funcs/riskResultsMarkFalsePositive.js";
import { riskResultsRevealPayload } from "../funcs/riskResultsRevealPayload.js";
import { riskResultsUnmarkFalsePositive } from "../funcs/riskResultsUnmarkFalsePositive.js";
import { riskResultsUnmask } from "../funcs/riskResultsUnmask.js";
import { ClientSDK, RequestOptions } from "../lib/sdks.js";
import { ListRiskResultsByChatResult } from "../models/components/listriskresultsbychatresult.js";
import { ListRiskResultsForAgentResult } from "../models/components/listriskresultsforagentresult.js";
import { ListRiskResultsResult } from "../models/components/listriskresultsresult.js";
import { RiskRevealPayloadResult } from "../models/components/riskrevealpayloadresult.js";
import { RiskUnmaskResultResult } from "../models/components/riskunmaskresultresult.js";
import {
  ListDismissedRiskResultsRequest,
  ListDismissedRiskResultsSecurity,
} from "../models/operations/listdismissedriskresults.js";
import {
  ListRiskResultsRequest,
  ListRiskResultsSecurity,
} from "../models/operations/listriskresults.js";
import {
  ListRiskResultsByChatRequest,
  ListRiskResultsByChatSecurity,
} from "../models/operations/listriskresultsbychat.js";
import {
  ListRiskResultsForAgentRequest,
  ListRiskResultsForAgentSecurity,
} from "../models/operations/listriskresultsforagent.js";
import {
  MarkRiskResultsFalsePositiveRequest,
  MarkRiskResultsFalsePositiveSecurity,
} from "../models/operations/markriskresultsfalsepositive.js";
import {
  RevealRiskResultPayloadRequest,
  RevealRiskResultPayloadSecurity,
} from "../models/operations/revealriskresultpayload.js";
import {
  UnmarkRiskResultsFalsePositiveRequest,
  UnmarkRiskResultsFalsePositiveSecurity,
} from "../models/operations/unmarkriskresultsfalsepositive.js";
import {
  UnmaskRiskResultRequest,
  UnmaskRiskResultSecurity,
} from "../models/operations/unmaskriskresult.js";
import { unwrapAsync } from "../types/fp.js";

export class Results extends ClientSDK {
  /**
   * listDismissedRiskResults risk
   *
   * @remarks
   * List suppressed risk results for the current project — findings hidden by an exclusion rule, a manual dismissal, or the automated false-positive sweep. Kept separate from listRiskResults, which never returns suppressed results.
   */
  async listDismissed(
    request?: ListDismissedRiskResultsRequest | undefined,
    security?: ListDismissedRiskResultsSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ListRiskResultsResult> {
    return unwrapAsync(riskResultsListDismissed(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listRiskResults risk
   *
   * @remarks
   * List risk analysis results for the current project.
   */
  async list(
    request?: ListRiskResultsRequest | undefined,
    security?: ListRiskResultsSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ListRiskResultsResult> {
    return unwrapAsync(riskResultsList(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listRiskResultsByChat risk
   *
   * @remarks
   * List risk results grouped by chat session for the current project.
   */
  async byChat(
    request?: ListRiskResultsByChatRequest | undefined,
    security?: ListRiskResultsByChatSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ListRiskResultsByChatResult> {
    return unwrapAsync(riskResultsByChat(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listRiskResultsForAgent risk
   *
   * @remarks
   * List risk analysis results with the `match` field redacted to an opaque length+sha256-prefix fingerprint. Matches the payload and pagination semantics of listRiskResults. Designed for AI assistant / MCP consumption so secret content (gitleaks captures, presidio entities, prompt-injection payloads) never reaches the model context. For shadow_mcp findings the `match` value — a non-sensitive server URL or command identifier — is passed through verbatim.
   */
  async listForAgent(
    request?: ListRiskResultsForAgentRequest | undefined,
    security?: ListRiskResultsForAgentSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ListRiskResultsForAgentResult> {
    return unwrapAsync(riskResultsListForAgent(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * markRiskResultsFalsePositive risk
   *
   * @remarks
   * Mark one or more risk results as manually-reviewed false positives. Distinct from exclusions: this suppresses the specific results picked, not future findings matching a rule.
   */
  async markFalsePositive(
    request: MarkRiskResultsFalsePositiveRequest,
    security?: MarkRiskResultsFalsePositiveSecurity | undefined,
    options?: RequestOptions,
  ): Promise<void> {
    return unwrapAsync(riskResultsMarkFalsePositive(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * revealRiskResultPayload risk
   *
   * @remarks
   * Return the full scanned payload of the MCP tool call phase a risk result was raised on, so its findings can be shown in context. Positions of every finding with the same execution_id and phase index into the returned payload. Requires an unrestricted chat:read grant. Every successful reveal is audited.
   */
  async revealPayload(
    request: RevealRiskResultPayloadRequest,
    security?: RevealRiskResultPayloadSecurity | undefined,
    options?: RequestOptions,
  ): Promise<RiskRevealPayloadResult> {
    return unwrapAsync(riskResultsRevealPayload(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * unmarkRiskResultsFalsePositive risk
   *
   * @remarks
   * Undo a false-positive dismissal for one or more risk results.
   */
  async unmarkFalsePositive(
    request: UnmarkRiskResultsFalsePositiveRequest,
    security?: UnmarkRiskResultsFalsePositiveSecurity | undefined,
    options?: RequestOptions,
  ): Promise<void> {
    return unwrapAsync(riskResultsUnmarkFalsePositive(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * unmaskRiskResult risk
   *
   * @remarks
   * Return the plaintext match for a single risk result on demand. Every finding requires chat:read for its attributed chat. MCP findings with an empty or invalid chat ID require an unrestricted chat:read grant and use encrypted stored evidence. Every successful reveal is audited.
   */
  async unmask(
    request: UnmaskRiskResultRequest,
    security?: UnmaskRiskResultSecurity | undefined,
    options?: RequestOptions,
  ): Promise<RiskUnmaskResultResult> {
    return unwrapAsync(riskResultsUnmask(
      this,
      request,
      security,
      options,
    ));
  }
}
